import React, { CSSProperties } from 'react';
import styled from '@emotion/styled';
import { observer } from 'mobx-react-lite';
import theme from './theme';
import { lighten } from 'polished';

const CheckboxContainer = styled.div`
    display: flex;
    align-items: center;
`;

const Icon = styled.svg`
    fill: none;
    stroke: white;
    stroke-width: 3px;
`;

const HiddenCheckbox = styled.input`
    border: 0;
    clip: rect(0 0 0 0);
    clip-path: inset(50%);
    height: 1px;
    margin: -1px;
    overflow: hidden;
    padding: 0;
    position: absolute;
    white-space: nowrap;
    width: 1px;
`;

const StyledCheckbox = styled.div<{ checked?: boolean; checkedColor?: string }>`
    display: inline-block;
    width: 16px;
    height: 16px;
    background-color: ${(props) =>
        props.checked ? props.checkedColor || theme.primary : theme.lightGrey};
    border-radius: 3px;
    transition: all 100ms ease-in;

    ${Icon} {
        visibility: ${(props) => (props.checked ? 'visible' : 'hidden')};
    }
`;
const CheckboxDescription = styled.div`
    display: flex;
    flex-direction: column;
    margin-left: 8px;

    span {
        font-weight: normal;
    }
    small {
        transition: color 150ms ease-in-out;
        font-style: italic;
        font-weight: 400;
        color: ${theme.grey};
    }
`;
const CheckboxLabel = styled.label<{ checkedColor?: string }>`
    display: flex;
    padding: 5px 0px;
    cursor: pointer;

    &:hover {
        ${StyledCheckbox} {
            box-shadow: 0 0 0 3px ${(props) => lighten(0.3, props.checkedColor || theme.primary)};
        }

        ${CheckboxDescription} {
            small {
                color: ${theme.mediumGrey};
            }
        }
    }
`;

interface Props {
    className?: string;
    checked?: boolean;
    checkedColor?: string;
    label?: string;
    labelStyle?: CSSProperties;
    description?: string;
    onChange: (() => void) | ((e: React.ChangeEvent<HTMLInputElement>) => void);
}

const Checkbox = observer<Props>(function Checkbox({
    className,
    checked,
    checkedColor,
    label,
    labelStyle,
    description,
    ...props
}) {
    return (
        <CheckboxLabel checkedColor={checkedColor}>
            <CheckboxContainer className={className}>
                <HiddenCheckbox checked={checked} {...props} type="checkbox" />
                <StyledCheckbox checked={checked} checkedColor={checkedColor}>
                    <Icon viewBox="0 0 24 24">
                        <polyline points="20 6 9 17 4 12" />
                    </Icon>
                </StyledCheckbox>
            </CheckboxContainer>
            <CheckboxDescription>
                <span style={labelStyle}>{label}</span>
                {description && <small>{description}</small>}
            </CheckboxDescription>
        </CheckboxLabel>
    );
});

export default Checkbox;
